import { ArrowDownIcon, ArrowUpIcon, GitBranchIcon, SplitIcon, Trash2Icon } from "lucide-react";
import type { CmmdProjectTaskDraftTask } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";

export interface ProjectLeadTaskTreeProps {
  readonly tasks: readonly CmmdProjectTaskDraftTask[];
  readonly selectedRef: string | null;
  readonly disabled: boolean;
  readonly atTaskLimit: boolean;
  readonly protectedRootRef: string;
  readonly onSelect: (localRef: string) => void;
  readonly onAddSubtask: (localRef: string) => void;
  readonly onMove: (localRef: string, direction: -1 | 1) => void;
  readonly onSplit: (localRef: string) => void;
  readonly onRemove: (localRef: string) => void;
}

export function ProjectLeadTaskTree(props: ProjectLeadTaskTreeProps) {
  return (
    <section
      data-project-lead-task-tree
      className="flex min-h-0 flex-col border-b border-border md:border-b-0 md:border-r"
    >
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-border px-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Task tree
          </p>
          <p className="text-xs text-muted-foreground">{props.tasks.length} proposed tasks</p>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2" aria-label="Proposed task tree">
        {props.tasks.map((task, index) => (
          <TaskTreeRow
            key={task.localRef}
            task={task}
            selected={task.localRef === props.selectedRef}
            first={index === 0}
            last={index === props.tasks.length - 1}
            disabled={props.disabled}
            atTaskLimit={props.atTaskLimit}
            protectedRootRef={props.protectedRootRef}
            onSelect={props.onSelect}
            onAddSubtask={props.onAddSubtask}
            onMove={props.onMove}
            onSplit={props.onSplit}
            onRemove={props.onRemove}
          />
        ))}
      </div>
    </section>
  );
}

interface TaskTreeRowProps {
  readonly task: CmmdProjectTaskDraftTask;
  readonly selected: boolean;
  readonly first: boolean;
  readonly last: boolean;
  readonly disabled: boolean;
  readonly atTaskLimit: boolean;
  readonly protectedRootRef: string;
  readonly onSelect: (localRef: string) => void;
  readonly onAddSubtask: (localRef: string) => void;
  readonly onMove: (localRef: string, direction: -1 | 1) => void;
  readonly onSplit: (localRef: string) => void;
  readonly onRemove: (localRef: string) => void;
}

function TaskTreeRow(props: TaskTreeRowProps) {
  const isRoot = props.task.parentTaskRef === null;
  return (
    <div
      className={
        props.selected
          ? "rounded-md border border-primary bg-primary/8"
          : "rounded-md border border-transparent bg-muted/35 hover:border-border hover:bg-muted/55"
      }
    >
      <button
        type="button"
        className={
          isRoot
            ? "flex w-full gap-2 p-2.5 text-left"
            : "flex w-full gap-2 py-2.5 pl-6 pr-2.5 text-left"
        }
        onClick={() => props.onSelect(props.task.localRef)}
      >
        <GitBranchIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">
            {props.task.title}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {isRoot ? "Parent task" : `Subtask of ${props.task.parentTaskRef}`}
          </span>
        </span>
        <span className="inline-flex min-h-5 items-center rounded border border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
          {props.task.priority}
        </span>
      </button>
      {props.selected ? <TaskTreeRowActions {...props} isRoot={isRoot} /> : null}
    </div>
  );
}

function TaskTreeRowActions(props: TaskTreeRowProps & { readonly isRoot: boolean }) {
  const growthDisabled = props.disabled || props.atTaskLimit;
  const growthTitle = props.atTaskLimit ? "Task trees are limited to 50 tasks." : undefined;
  return (
    <div className="flex items-center gap-[8px] overflow-x-auto border-t border-primary/20 px-2 py-2 md:gap-1 md:py-1.5">
      <Button
        size="xs"
        variant="ghost"
        className={RESPONSIVE_TEXT_BUTTON_CLASS}
        disabled={growthDisabled}
        title={growthTitle}
        onClick={() => props.onAddSubtask(props.task.localRef)}
      >
        Add subtask
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        className={RESPONSIVE_ICON_BUTTON_CLASS}
        aria-label="Move task up"
        disabled={props.disabled || props.first}
        onClick={() => props.onMove(props.task.localRef, -1)}
      >
        <ArrowUpIcon aria-hidden />
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        className={RESPONSIVE_ICON_BUTTON_CLASS}
        aria-label="Move task down"
        disabled={props.disabled || props.last}
        onClick={() => props.onMove(props.task.localRef, 1)}
      >
        <ArrowDownIcon aria-hidden />
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        className={RESPONSIVE_ICON_BUTTON_CLASS}
        aria-label="Split task"
        disabled={growthDisabled}
        title={growthTitle}
        onClick={() => props.onSplit(props.task.localRef)}
      >
        <SplitIcon aria-hidden />
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        className={RESPONSIVE_ICON_BUTTON_CLASS}
        aria-label="Remove task"
        disabled={props.disabled || props.task.localRef === props.protectedRootRef}
        onClick={() => props.onRemove(props.task.localRef)}
      >
        <Trash2Icon aria-hidden />
      </Button>
    </div>
  );
}
